import React from "react";
import { Navigate } from "react-router-dom";

const MAINTENANCE_HOST = "public.business.chargingadda.com";

export const isMaintenanceMode = (): boolean => {
  const hostname = window.location.hostname;
  // Allow in development (localhost)
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  return hostname === MAINTENANCE_HOST;
};

const userRoles = (): string[] => {
  try {
    const stored = localStorage.getItem("user_info");
    if (!stored) return [];
    const user = JSON.parse(stored);
    return Array.isArray(user?.roles) ? user.roles : [];
  } catch {
    return [];
  }
};

export const hasRole = (role: string): boolean => userRoles().includes(role);

export const isSuperAdmin = (): boolean => hasRole("super-admin");

// Read-only maintenance access (sees a subset of sections, no actions).
export const isMaintenanceViewer = (): boolean => hasRole("maintenance-viewer");

// Marketing: runs cashback campaigns and nothing else in the console.
export const isCashbackManager = (): boolean => hasRole("cashback-manager");

// May ENTER the maintenance console (super-admin, read-only viewer, or the
// cashback manager -- who lands on the cashback console only).
export const canAccessMaintenance = (): boolean =>
  isSuperAdmin() || isMaintenanceViewer() || isCashbackManager();

// Campaigns: draft, forecast, schedule, raise a lead-time waiver.
export const canManageCampaigns = (): boolean => isSuperAdmin() || isCashbackManager();
// Approve / reject a lead-time waiver, end a live campaign: super-admin only.
export const canApproveWaivers = (): boolean => isSuperAdmin();

// May perform MUTATIONS in the maintenance console (super-admin only).
export const canEditMaintenance = (): boolean => isSuperAdmin();

// Sections a maintenance-viewer must NOT see at all (super-admin only):
// audit log, remote-command console, and Zitadel provisioning.
export const canViewMaintenanceAudit = (): boolean => isSuperAdmin();
export const canViewMaintenanceProvisioning = (): boolean => isSuperAdmin();
export const canUseRemoteCommands = (): boolean => isSuperAdmin();

interface MaintenanceGuardProps {
  children: React.ReactNode;
  // When true, only super-admin may view (used for audit / provisioning routes).
  superAdminOnly?: boolean;
}

const MaintenanceGuard: React.FC<MaintenanceGuardProps> = ({
  children,
  superAdminOnly = false,
}) => {
  if (!isMaintenanceMode()) {
    return <Navigate to="/dashboard" replace />;
  }
  const allowed = superAdminOnly ? isSuperAdmin() : canAccessMaintenance();
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

export default MaintenanceGuard;
